import { ViewEnsaioAliquotaAtual } from '@models/views/view-ensaio-aliquota-atual.model';
import { meanBy } from 'lodash';
import { std } from 'mathjs';
import { CONVERSAO_PPM_SOPT } from '../../../back/src/amostras/amostras.constants';
import { CategoriaEnsaioAliquotaEnum } from '../../../back/src/ensaios-aliquotas/ensaio-aliquota.constants';
import { TipoEnsaioEnum } from '../../../back/src/ensaios-metodos/ensaios-metodos.constants';

export enum UnidadeResultadoLaudoEnum {
  PPM = 'PPM',
  PORCENTAGEM = 'PORCENTAGEM',
}

export interface AliquotaLaudo {
  id: number;
  identificacao: string;
  finalizada: boolean;
  _selecionada: boolean;
}

export interface AliquotaCalcinacao extends AliquotaLaudo {
  pesoInicialG: number;
  pesoFinalG: number;
}
export interface AliquotaUmidade extends AliquotaLaudo {
  pesoInicialG: number;
  pesoFinalG: number;
  porcentagemUmidade: number;
}

export interface AliquotaGravimetria extends AliquotaLaudo {
  categoria: CategoriaEnsaioAliquotaEnum;
  massaAmostraG: number;
  massaPerolaMg: number;
  concentracaoAu: number;
}

export interface AliquotaIcp extends AliquotaLaudo {
  categoria: CategoriaEnsaioAliquotaEnum;
  massaAmostraG: number;
  massaPerolaMg: number;
  diluicaoMl: number;
  elementos: ElementoAliquota[];
}

export interface AliquotaIcpCobre extends AliquotaLaudo {
  categoria: CategoriaEnsaioAliquotaEnum;
  massaAmostraG: number;
  diluicaoMl: number;
  elementos: ElementoAliquota[];
}

export interface ElementoAliquota {
  elemento: string;
  concentracao: number;
  resultado: number;
  selecionado: boolean;
}

export interface EnsaioLaudo {
  id: number;
  tipo: TipoEnsaioEnum;
  data: Date;
}

export interface EnsaioCalcinacao extends EnsaioLaudo {
  aliquotas: AliquotaCalcinacao[];
}
export interface EnsaioUmidade extends EnsaioLaudo {
  aliquotas: AliquotaUmidade[];
}

export interface EnsaioGravimetria extends EnsaioLaudo {
  aliquotas: AliquotaGravimetria[];
}

export interface EnsaioGenerico extends EnsaioLaudo {
  aliquotas: AliquotaGravimetria[] | AliquotaIcp[] | AliquotaIcpCobre[];
}

export interface EnsaioIcp extends EnsaioLaudo {
  aliquotas: AliquotaIcp[];
}

export interface EnsaioIcpCobre extends EnsaioLaudo {
  aliquotas: AliquotaIcpCobre[];
}

export interface ResultadoLaudo {
  elemento: string;
  concentracao: number;
  unidade: UnidadeResultadoLaudoEnum;
  categoria: CategoriaEnsaioAliquotaEnum | null;
  reportar: boolean;
}

export interface DadosLaudo {
  auGravimetria: boolean;
  auIcp: boolean;
  resultados: ResultadoLaudo[];
}

export interface AssayCalculation {
  sampleWeightKg: number;
  burnLossKg: number;
  afterRoastNKg: number;
  metallicsGKg: number;
  metallicsG1Kg: number;
  sweepsJKg: number;
  afterSplitF1Kg: number;
  sweepsCutHKg: number;
  copperAddedD1Kg: number;
  ingotC1Kg: number;
  sweepsMetallicsBKg: number;
  sweepsAKg: number;
  cobrePistola: number;
  elementosAdded: ElementosAdded[];
}

export interface ElementosAdded {
  elemento: string;
  valor: number | null;
}

export function getConcentracaoElemento(
  elemento: string,
  aliquota: AliquotaIcp | AliquotaIcpCobre,
): number {
  return (
    aliquota.elementos.find((el) => el.elemento === elemento)?.concentracao ??
    null
  );
}

export function getResultadoElemento(
  elemento: string,
  aliquota: AliquotaIcp | AliquotaIcpCobre,
): number {
  return (
    aliquota.elementos.find((el) => el.elemento === elemento)?.resultado ?? null
  );
}

export function calculaAuGravimetria(
  aliquota: ViewEnsaioAliquotaAtual,
  fator: number,
): number {
  return (
    ((100 * (aliquota?.logResultadosAtualResultados?.massaPerolaMg / 1000)) /
      aliquota?.logResultadosAtualResultados?.massaAmostraG) *
    10000 *
    fator
  );
}

export function calculaAgGravimetria(
  aliquotasGravimetria: AliquotaGravimetria[],
  aliquotasIcp: (AliquotaIcp | AliquotaIcpCobre)[],
  fator: number,
  elementos: string[],
): number {
  return (
    ((100 * calculaMediaCampo(aliquotasIcp, 'massaPerolaMg')) /
      1000 /
      calculaMediaCampo(aliquotasIcp, 'massaAmostraG')) *
      10000 *
      fator -
    (calculaMediaAuGravimetria(aliquotasGravimetria) +
      somaResultadoElementos(aliquotasIcp, elementos))
  );
}

export function somaResultadoElementos(
  aliquotasIcp: (AliquotaIcp | AliquotaIcpCobre)[],
  elementos: string[],
): number {
  if (elementos.length === 0) return 0;
  return elementos.reduce(
    (aux, elemento) => aux + calculaResultadoMedio(aliquotasIcp, elemento),
    0,
  );
}

export function calculaMediaAuGravimetria(
  aliquotas: AliquotaGravimetria[],
): number {
  if (aliquotas.length === 0) return 0;
  return meanBy(
    aliquotas,
    (aliquota: AliquotaGravimetria) => aliquota.concentracaoAu,
  );
}

export function verificacaoBalancaGravimetria(
  aliquotas: AliquotaGravimetria[],
): number {
  if (aliquotas.length === 0) return 0;
  return (
    std(
      aliquotas.map(
        (aliquota: AliquotaGravimetria) => aliquota.concentracaoAu,
      ) as any,
    ) / calculaMediaAuGravimetria(aliquotas)
  );
}

export function calculaResultadoElemento(
  aliquota: ViewEnsaioAliquotaAtual,
  concentracao: number,
  fator: number,
): number {
  return (
    ((concentracao * aliquota?.logResultadosAtualResultados?.diluicaoMl) /
      aliquota?.logResultadosAtualResultados?.massaAmostraG) *
    fator
  );
}

export function calculaResultadoCu(
  aliquota: ViewEnsaioAliquotaAtual,
  concentracao: number,
  fator: number,
): number {
  return (
    (((concentracao * 1000) /
      aliquota?.logResultadosAtualResultados?.massaAmostraG) *
      fator) /
    10000
  );
}

export function calculaConcentracaoMedia(
  aliquotas: (AliquotaIcp | AliquotaIcpCobre)[],
  elemento: string,
): number {
  return meanBy(
    aliquotas.filter(
      (aliquota) =>
        !!aliquota.elementos.find(
          (el) => el.elemento === elemento && el.selecionado,
        ),
    ),
    (aliquota) => getConcentracaoElemento(elemento, aliquota) || 0,
  );
}

export function calculaResultadoMedio(
  aliquotas: (AliquotaIcp | AliquotaIcpCobre)[],
  elemento: string,
): number {
  return meanBy(
    aliquotas.filter(
      (aliquota) =>
        !!aliquota.elementos.find(
          (el) => el.elemento === elemento && el.selecionado,
        ),
    ),
    (aliquota) => getResultadoElemento(elemento, aliquota) || 0,
  );
}

export function calculaMediaCampo(
  aliquotas: (AliquotaIcp | AliquotaIcpCobre)[],
  campo: string,
): number {
  if (aliquotas.length === 0) return 0;
  return meanBy(aliquotas, (aliquota: AliquotaIcp) => aliquota[campo]);
}

export function calculaDiferenca(
  mediaGravimetria: number,
  mediaIcp: number,
): number {
  return (mediaGravimetria - mediaIcp) / mediaGravimetria;
}

export function convertPpmToPercent(value: number): number {
  return value / 10 ** 4;
}

export function convertPercentToPpm(value: number): number {
  return value * 10 ** 4;
}
export function convertPpmToSopt(value: number): number {
  return value * CONVERSAO_PPM_SOPT;
}

export function convertSoptToPpm(value: number): number {
  return value / CONVERSAO_PPM_SOPT;
}

export function calculaAssayGlencore(
  preparationDetails: AssayCalculation,
  elemento: string,
  aliquotasGravimetria: AliquotaGravimetria[],
  aliquotasMetallic: (AliquotaIcp | AliquotaIcpCobre)[],
  aliquotasPulp: (AliquotaIcp | AliquotaIcpCobre)[],
  aliquotasIcpPulp: AliquotaIcp[],
  formRevisao: any,
  demaisElementosIcp: string[],
): number {
  let calculo1 = 0;
  let calculo2 = 0;
  let elementoMetallic = 0;
  let elementoPulp = 0;
  const med = [];

  const elementoAddedValor =
    preparationDetails.elementosAdded.find((el) => el.elemento === elemento)
      ?.valor || 0;
  if (elemento === 'Cu') {
    elementoMetallic = preparationDetails.cobrePistola / 100;
  } else {
    elementoMetallic = convertPpmToSopt(
      calculaResultadoMedio(aliquotasMetallic, elemento),
    );
  }
  if (elemento === 'Au') {
    if (formRevisao.auGravimetria) {
      if (aliquotasGravimetria.length > 0)
        med.push(calculaMediaAuGravimetria(aliquotasGravimetria));
    }
    if (formRevisao.auIcp) {
      if (aliquotasPulp.length > 0)
        med.push(calculaResultadoMedio(aliquotasPulp, 'Au'));
    }
    if (med.length > 1) {
      elementoPulp = convertPpmToSopt(meanBy(med, (el) => el)) || 0;
    } else {
      elementoPulp = convertPpmToSopt(med[0]) || 0;
    }
  } else if (elemento === 'Ag') {
    if (formRevisao.agGravimetria) {
      elementoPulp = convertPpmToSopt(
        calculaAgGravimetria(
          aliquotasGravimetria,
          aliquotasIcpPulp,
          1,
          demaisElementosIcp,
        ),
      );
    }

    if (formRevisao.agIcp) {
      elementoPulp = convertPpmToSopt(
        calculaResultadoMedio(aliquotasIcpPulp, elemento),
      );
    }
  } else if (elemento === 'Cu') {
    elementoPulp =
      convertPpmToPercent(calculaResultadoMedio(aliquotasPulp, elemento)) / 100;
  } else {
    elementoPulp = convertPpmToSopt(
      calculaResultadoMedio(aliquotasPulp, elemento),
    );
  }
  if (elemento === 'Cu') {
    if (preparationDetails.metallicsG1Kg) {
      const res1 =
        (elementoMetallic *
          preparationDetails.ingotC1Kg *
          preparationDetails.metallicsG1Kg) /
          preparationDetails.afterSplitF1Kg /
          preparationDetails.sampleWeightKg -
        ((elementoAddedValor / 100) *
          preparationDetails.copperAddedD1Kg *
          preparationDetails.metallicsG1Kg) /
          preparationDetails.afterSplitF1Kg /
          preparationDetails.sampleWeightKg;

      if (res1 > 0) calculo1 = res1;
    }
    if (preparationDetails.sweepsJKg) {
      const res2 =
        (elementoPulp *
          (preparationDetails.sweepsAKg / preparationDetails.sweepsCutHKg) *
          preparationDetails.sweepsJKg) /
        preparationDetails.sampleWeightKg;
      if (res2 > 0) calculo2 = res2;
    }
  } else {
    if (preparationDetails.metallicsG1Kg) {
      const res1 =
        (((((elementoMetallic * preparationDetails.ingotC1Kg) / 2000) *
          preparationDetails.metallicsG1Kg) /
          preparationDetails.afterSplitF1Kg) *
          2000) /
          preparationDetails.sampleWeightKg -
        (((((elementoAddedValor * preparationDetails.copperAddedD1Kg) / 2000) *
          preparationDetails.metallicsG1Kg) /
          preparationDetails.afterSplitF1Kg) *
          2000) /
          preparationDetails.sampleWeightKg;
      if (res1 > 0) calculo1 = res1;
    }
    if (preparationDetails.sweepsJKg) {
      const res2 =
        elementoPulp *
        (preparationDetails.sweepsAKg / preparationDetails.sweepsCutHKg) *
        (preparationDetails.sweepsJKg / 2000) *
        (2000 / preparationDetails.sampleWeightKg);

      if (res2 > 0) calculo2 = res2;
    }
  }
  if (elemento === 'Cu') {
    return (calculo1 + calculo2) * 100;
  }
  return calculo1 + calculo2;
}
